import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import PublicHeader from "@/components/PublicHeader";
import PublicFooter from "@/components/PublicFooter";
import { Wind, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

function DailyChart({ daily }) {
  if (!daily.length) return null;
  const w = 800, h = 200, padL = 30, padR = 10, padT = 10, padB = 30;
  const inner = { w: w - padL - padR, h: h - padT - padB };
  const vals = daily.map((d) => d.avg);
  const min = Math.min(0, ...vals);
  const max = Math.max(200, ...vals);
  const range = max - min || 1;
  const stepX = inner.w / (daily.length - 1);
  const y = (v) => padT + inner.h - ((v - min) / range) * inner.h;
  const pts = daily.map((d, i) => `${padL + i * stepX},${y(d.avg)}`).join(" ");
  const yTicks = [0, 50, 100, 150, 200, 300];
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        {/* threshold bands */}
        {[
          { from: 0, to: 50, color: "#10B981" },
          { from: 50, to: 100, color: "#84CC16" },
          { from: 100, to: 150, color: "#F59E0B" },
          { from: 150, to: 200, color: "#EF4444" },
        ].map((b, i) => (
          <rect key={i} x={padL} y={y(Math.min(b.to, max))} width={inner.w} height={Math.max(0, y(b.from) - y(Math.min(b.to, max)))} fill={b.color} fillOpacity="0.06"/>
        ))}
        {yTicks.filter((t) => t <= max).map((t) => (
          <g key={t}>
            <line x1={padL} y1={y(t)} x2={w - padR} y2={y(t)} stroke="#E2E8F0" strokeDasharray="2 3"/>
            <text x={padL - 6} y={y(t) + 3} textAnchor="end" fontSize="9" fill="#64748B">{t}</text>
          </g>
        ))}
        <polyline fill="none" stroke="#0F172A" strokeWidth="1.6" points={pts}/>
        {daily.map((d, i) => (
          <g key={d.date}>
            <circle cx={padL + i * stepX} cy={y(d.avg)} r="3.5" fill={d.color}/>
            <text x={padL + i * stepX} y={h - padB + 15} textAnchor="middle" fontSize="9" fill="#475569">{d.date.slice(5)}</text>
          </g>
        ))}
      </svg>
    </div>
  );
}

export default function AqiHistoryPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  useEffect(() => { setData(null); api.get(`/aqi/history?days=${days}`).then((r) => setData(r.data)).catch(() => {}); }, [days]);
  const summary = useMemo(() => {
    if (!data?.daily?.length) return null;
    const vals = data.daily.map((d) => d.avg);
    return {
      avg: Math.round(vals.reduce((a, b) => a + b, 0) / vals.length),
      max: Math.max(...vals), min: Math.min(...vals),
      good_days: data.daily.filter((d) => d.avg <= 100).length,
    };
  }, [data]);

  return (
    <>
      <PublicHeader/>
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4 py-10">
          <div className="flex items-center gap-2 mb-2">
            <Wind className="w-5 h-5 text-emerald-300"/>
            <span className="uppercase tracking-[0.28em] text-[11px] text-emerald-300 font-black">Air Quality Trend</span>
          </div>
          <h1 className="font-devnagri-heading text-3xl md:text-4xl font-black">वायु गुणवत्ता का इतिहास</h1>
          <p className="text-slate-300 mt-2">सूरजपुर, छत्तीसगढ़ · परिवार और स्कूली दिन की योजना के लिए</p>
        </div>
      </section>
      <main className="max-w-6xl mx-auto px-4 py-8" data-testid="aqi-history-page">
        <div className="flex items-center gap-3 mb-5">
          {[7, 14, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
              className={`px-4 py-1.5 rounded-full text-sm font-bold transition ${days === d ? "bg-[#0F172A] text-white" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}
              data-testid={`aqi-range-${d}`}>{d} दिन</button>
          ))}
        </div>
        {!data ? (
          <div className="p-10 text-center text-slate-500">लोड हो रहा है…</div>
        ) : summary && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
              <div className="bg-white border rounded-lg p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">औसत AQI</div><div className="text-2xl font-black text-[#0F172A]">{summary.avg}</div></div>
              <div className="bg-white border rounded-lg p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">अधिकतम</div><div className="text-2xl font-black text-rose-600">{summary.max}</div></div>
              <div className="bg-white border rounded-lg p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">न्यूनतम</div><div className="text-2xl font-black text-emerald-600">{summary.min}</div></div>
              <div className="bg-white border rounded-lg p-4"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">अच्छे दिन</div><div className="text-2xl font-black text-emerald-700">{summary.good_days} / {data.daily.length}</div></div>
            </div>
            <DailyChart daily={data.daily}/>
            <div className="mt-5 bg-white border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
                  <tr><th className="text-left px-4 py-2">तारीख़</th><th className="text-center px-4 py-2">औसत</th><th className="text-center px-4 py-2">↑ Max</th><th className="text-center px-4 py-2">↓ Min</th><th className="text-center px-4 py-2">PM2.5</th><th className="text-left px-4 py-2">स्तर</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {[...data.daily].reverse().map((d) => (
                    <tr key={d.date}>
                      <td className="px-4 py-2 font-mono text-xs">{d.date}</td>
                      <td className="px-4 py-2 text-center font-black" style={{ color: d.color }}>{d.avg}</td>
                      <td className="px-4 py-2 text-center text-slate-700">{d.max}</td>
                      <td className="px-4 py-2 text-center text-slate-700">{d.min}</td>
                      <td className="px-4 py-2 text-center text-slate-500">{d.pm2_5 ?? "—"}</td>
                      <td className="px-4 py-2"><span className="text-xs font-bold" style={{ color: d.color }}>{d.level}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-slate-500 hover:text-[#0F172A] text-sm"><ArrowLeft className="w-4 h-4"/>मुखपृष्ठ</Link>
      </main>
      <PublicFooter/>
    </>
  );
}
